import React from "react";
import { Link } from "@/lib/router-compat";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import CrisisLifelineBanner from "@/components/CrisisLifelineBanner";
import SEOHead from "@/components/SEOHead";
import Breadcrumbs from "@/components/Breadcrumbs";
import { RelatedPages, type RelatedLink } from "@/components/RelatedPages";
import { Crumb } from "@/components/schemaConstants";
import { SaintDisclaimerNotice } from "@/components/SaintDisclaimerNotice";
import SaintFiveChecksBox from "@/components/SaintFiveChecksBox";
import type {
  LearnSection,
  LearnFAQ,
  LearnSource,
  LearnReviewBlock,
} from "@/pages/learn/learnShared";

export interface LearnPageLayoutEsProps {
  title: string;
  description: string;
  canonical: string;
  h1: string;
  breadcrumbLabel: string;
  crumbs: Crumb[];
  shortAnswer: string;
  keyTakeaways: string[];
  sections: LearnSection[];
  faqs: LearnFAQ[];
  sources: LearnSource[];
  schemaJson: object;
  relatedLinks: RelatedLink[];
  reviewBlock?: LearnReviewBlock;
}

const PHONE = "954-827-9793";

const CrisisNotice: React.FC = () => (
  <aside
    className="not-prose my-8 rounded-xl border border-destructive/30 bg-destructive/5 p-5"
    role="note"
  >
    <p className="text-sm font-medium text-primary">
      Si está pensando en hacerse daño, llame o envíe un mensaje de texto al{" "}
      <a
        href="tel:988"
        className="font-bold text-destructive underline underline-offset-2"
      >
        988
      </a>{" "}
      (Línea de Prevención del Suicidio y Crisis) en EE. UU., o acuda a la sala
      de emergencias más cercana.
    </p>
  </aside>
);

/**
 * Spanish-language learn article layout. Mirrors LearnPageLayout but with
 * Spanish UI chrome. Content (h1, shortAnswer, sections, faqs, sources) is
 * passed in already translated. Clinical claims, citations and stats are
 * preserved verbatim from the English source — only the surrounding prose
 * and UI strings are translated.
 */
const LearnPageLayoutEs: React.FC<LearnPageLayoutEsProps> = ({
  title,
  description,
  canonical,
  h1,
  crumbs,
  shortAnswer,
  keyTakeaways,
  sections,
  faqs,
  sources,
  schemaJson,
  relatedLinks,
  reviewBlock,
}) => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={title}
        description={description}
        canonical={canonical}
        schemaJson={[schemaJson]}
        keywords="TMS, estimulación magnética transcraneal, tratamiento de la depresión, SAINT, Neuro Wellness TMS"
      />
      <Navbar />
      <main>
        {/* Hero / header */}
        <section className="bg-gradient-hero py-10 sm:py-14">
          <div className="container-px mx-auto max-w-3xl">
            <Breadcrumbs crumbs={crumbs} className="mb-6" />
            <h1 className="font-headings text-2xl font-extrabold leading-tight text-primary sm:text-3xl md:text-4xl">
              {h1}
            </h1>
            {reviewBlock && (
              <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {reviewBlock.writtenBy && (
                  <span>Escrito por {reviewBlock.writtenBy}</span>
                )}
                {reviewBlock.reviewedBy && (
                  <span>
                    Revisado médicamente por{" "}
                    <span className="font-medium text-primary">
                      {reviewBlock.reviewedBy}
                    </span>
                  </span>
                )}
                {reviewBlock.published && (
                  <span>Publicado {reviewBlock.published}</span>
                )}
                {reviewBlock.lastReviewed && (
                  <span>Última revisión médica {reviewBlock.lastReviewed}</span>
                )}
                {reviewBlock.lastUpdated && (
                  <span>Última actualización {reviewBlock.lastUpdated}</span>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Article body */}
        <section className="py-10 sm:py-12">
          <div className="container-px mx-auto max-w-3xl">
            {/* Short answer */}
            <div className="mb-8 rounded-2xl border border-accent/30 bg-accent/5 p-5 sm:p-6">
              <p className="text-base leading-relaxed text-foreground sm:text-[1.0625rem]">
                {shortAnswer}
              </p>
            </div>

            {/* Key takeaways */}
            <div className="mb-10">
              <h2 className="font-headings text-lg font-bold text-primary mb-3">
                Puntos clave
              </h2>
              <ul className="space-y-2">
                {keyTakeaways.map((t, i) => (
                  <li key={i} className="flex gap-3 text-foreground">
                    <span
                      aria-hidden="true"
                      className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent"
                    />
                    <span className="text-[1.0625rem] leading-relaxed">
                      {t}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Prose sections */}
            <div className="space-y-8">
              {sections.map((s, i) => (
                <div key={i}>
                  {s.heading && (
                    <h2 className="font-headings text-xl font-bold text-primary mb-3 sm:text-2xl">
                      {s.heading}
                    </h2>
                  )}
                  <div className="space-y-4 text-[1.0625rem] leading-relaxed text-foreground">
                    {s.body}
                  </div>
                </div>
              ))}
            </div>

            {/* ¿Es realmente SAINT®? 5 comprobaciones — en cada artículo /es/learn */}
            <div className="mt-10">
              <SaintFiveChecksBox es />
            </div>

            {/* FAQ */}
            <div className="mt-12">
              <h2 className="font-headings text-xl font-bold text-primary mb-5 sm:text-2xl">
                Preguntas frecuentes
              </h2>
              <div className="space-y-3">
                {faqs.map((f, i) => (
                  <details
                    key={i}
                    className="group rounded-xl border border-border bg-card p-5 shadow-soft [&_p]:text-[1.0625rem] [&_p]:leading-relaxed"
                  >
                    <summary className="flex cursor-pointer items-center justify-between font-headings text-base font-bold text-primary marker:content-['']">
                      {f.question}
                      <span
                        aria-hidden="true"
                        className="ml-3 shrink-0 text-accent transition-transform group-open:rotate-45"
                      >
                        +
                      </span>
                    </summary>
                    <p className="mt-3 text-foreground">{f.answer}</p>
                  </details>
                ))}
              </div>
            </div>

            {/* Crisis notice */}
            <CrisisNotice />

            {/* Sources */}
            {sources.length > 0 && (
              <div className="mt-8">
                <h2 className="font-headings text-base font-bold text-primary mb-3">
                  Referencias médicas y fuentes
                </h2>
                <ol className="space-y-1.5 text-sm text-muted-foreground">
                  {sources.map((s, i) => (
                    <li key={i} className="leading-relaxed">
                      <span className="font-medium text-foreground">
                        {i + 1}.{" "}
                      </span>
                      {s.url ? (
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noopener"
                          className="break-words underline underline-offset-2 hover:text-accent"
                        >
                          {s.citation}
                        </a>
                      ) : (
                        s.citation
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Aviso de SAINT® — en cada artículo /es/learn */}
            <SaintDisclaimerNotice es />
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-navy py-12 sm:py-14">
          <div className="container-px mx-auto max-w-3xl text-center">
            <h2 className="font-headings text-2xl font-bold text-white sm:text-3xl">
              ¿Tiene preguntas sobre si la TMS podría ser adecuada para usted?
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-white/85">
              Hable con nuestro equipo clínico. Una consulta gratuita es la
              mejor manera de obtener respuestas específicas para su situación.
            </p>
            <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Link
                to="/es/saint"
                className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-accent px-6 py-3 font-bold text-accent-foreground shadow-glow transition hover:opacity-90 sm:w-auto"
              >
                Solicitar una consulta gratuita
              </Link>
              <a
                href={`tel:${PHONE.replace(/-/g, "")}`}
                className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg border border-white/40 px-6 py-3 font-semibold text-white transition hover:bg-white/10 sm:w-auto"
              >
                Llamar al {PHONE}
              </a>
            </div>
          </div>
        </section>

        <RelatedPages
          heading="Educación para pacientes relacionada"
          links={relatedLinks}
        />
      </main>
      <div className="container-px mx-auto max-w-3xl px-4 py-6">
        <CrisisLifelineBanner />
      </div>
      <Footer />
    </div>
  );
};

export default LearnPageLayoutEs;
